// src/pages/ParametresPage.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api'; // ← axios instance avec JWT auto-injecté

// ── Utilitaires ──────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="pm-toggle" aria-checked={checked}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <span className="pm-toggle-track">
        <span className="pm-toggle-thumb" />
      </span>
    </label>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="pm-field">
      <label className="pm-label">{label}</label>
      {children}
      {hint && <span className="pm-hint">{hint}</span>}
    </div>
  );
}

function Input({ value, onChange, type = 'text', disabled, placeholder }) {
  return (
    <input
      className="pm-input"
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="pm-card">
      <div className="pm-card-header">
        <span className="pm-card-icon">{icon}</span>
        <h3 className="pm-card-title">{title}</h3>
      </div>
      <div className="pm-card-body">{children}</div>
    </div>
  );
}

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  const colors = { success: '#1b5e20', error: '#b71c1c', info: '#0d47a1' };
  const bg     = { success: '#e8f5e9', error: '#ffebee', info: '#e3f2fd' };
  return (
    <div style={{
      background: bg[type], color: colors[type],
      borderLeft: `4px solid ${colors[type]}`,
      padding: '10px 16px', borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 16, fontSize: 14,
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors[type], fontWeight: 700, fontSize: 16 }}>×</button>
    </div>
  );
}

// ── Onglets ───────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'organisation', label: 'Organisation',    icon: '🏢' },
  { key: 'rh',           label: 'RH & Paie',       icon: '👥' },
  { key: 'conges',       label: 'Congés',          icon: '📅' },
  { key: 'securite',     label: 'Sécurité',        icon: '🔒' },
  { key: 'notifications',label: 'Notifications',   icon: '🔔' },
  { key: 'systeme',      label: 'Système',         icon: '⚙️' },
  { key: 'projets',      label: 'Projets / Bailleurs', icon: '📁' },
  { key: 'departements', label: 'Départements',    icon: '🏗️' },
  { key: 'fonctions',    label: 'Fonctions',       icon: '🎯' },
  { key: 'categories',   label: 'Catégories prof.', icon: '📊' },
];

// ── Rendu des sections ────────────────────────────────────────────────────────

function OrgSection({ data, onChange }) {
  const f = (k) => <Input value={data[k]} onChange={v => onChange(k, v)} />;
  return (
    <>
      <SectionCard title="Identité de l'organisation" icon="🏢">
        <div className="pm-grid-2">
          <Field label="Nom complet">{f('nom')}</Field>
          <Field label="Sigle">{f('sigle')}</Field>
          <Field label="Code bureau">{f('code_bureau')}</Field>
          <Field label="Pays">{f('pays')}</Field>
          <Field label="Ville">{f('ville')}</Field>
          <Field label="Adresse">{f('adresse')}</Field>
          <Field label="Téléphone">{f('telephone')}</Field>
          <Field label="Email RH">{f('email_rh')}</Field>
          <Field label="Site web">{f('site_web')}</Field>
        </div>
      </SectionCard>
      <SectionCard title="Localisation & Format" icon="🌍">
        <div className="pm-grid-3">
          <Field label="Devise">
            <select className="pm-input" value={data.devise || 'XOF'} onChange={e => onChange('devise', e.target.value)}>
              <option value="XOF">XOF — Franc CFA</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollar</option>
              <option value="GNF">GNF — Franc Guinéen</option>
            </select>
          </Field>
          <Field label="Langue">
            <select className="pm-input" value={data.langue || 'fr'} onChange={e => onChange('langue', e.target.value)}>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </Field>
          <Field label="Fuseau horaire">
            <select className="pm-input" value={data.timezone || 'Africa/Abidjan'} onChange={e => onChange('timezone', e.target.value)}>
              <option value="Africa/Abidjan">Africa/Abidjan (GMT+0)</option>
              <option value="Africa/Dakar">Africa/Dakar (GMT+0)</option>
              <option value="Africa/Lagos">Africa/Lagos (GMT+1)</option>
              <option value="Europe/Paris">Europe/Paris (GMT+1/+2)</option>
            </select>
          </Field>
        </div>
      </SectionCard>
    </>
  );
}

function RhSection({ data, onChange }) {
  const f = (k, type='text') => <Input value={data[k]} onChange={v => onChange(k, v)} type={type} />;
  const t = (k) => <Toggle checked={data[k]} onChange={v => onChange(k, v)} />;
  return (
    <>
      <SectionCard title="Temps de travail" icon="⏱️">
        <div className="pm-grid-3">
          <Field label="Début année fiscale (mois)">{f('annee_fiscale_debut', 'number')}</Field>
          <Field label="Heures / semaine">{f('nb_heures_semaine', 'number')}</Field>
          <Field label="Jours travaillés / semaine">{f('nb_jours_travail', 'number')}</Field>
        </div>
      </SectionCard>
      <SectionCard title="Cotisations & Taxes" icon="💰">
        <div className="pm-grid-3">
          <Field label="Salaire minimum (FCFA)">{f('salaire_min', 'number')}</Field>
          <Field label="Plafond CNPS (FCFA)">{f('plafond_cnps', 'number')}</Field>
          <Field label="Taux CNPS employé (%)">{f('taux_cnps_employe', 'number')}</Field>
          <Field label="Taux CNPS employeur (%)">{f('taux_cnps_employeur', 'number')}</Field>
          <Field label="Taux IS (%)">{f('taux_is', 'number')}</Field>
          <Field label="Taux CF (%)">{f('taux_cf', 'number')}</Field>
        </div>
      </SectionCard>
      <SectionCard title="Contrats & Bulletins" icon="📄">
        <div className="pm-grid-3">
          <Field label="Délai essai cadre (mois)">{f('delai_essai_cadre', 'number')}</Field>
          <Field label="Délai essai non-cadre (mois)">{f('delai_essai_nonCadre', 'number')}</Field>
          <Field label="Délai préavis CDI (mois)">{f('delai_preavis_cdi', 'number')}</Field>
        </div>
        <div className="pm-grid-3" style={{ marginTop: 16 }}>
          <Field label="Bulletin automatique"><div className="pm-toggle-row">{t('bulletin_auto')}<span>{data.bulletin_auto ? 'Oui' : 'Non'}</span></div></Field>
          <Field label="Signature DG"><div className="pm-toggle-row">{t('signature_dg')}<span>{data.signature_dg ? 'Activée' : 'Désactivée'}</span></div></Field>
          <Field label="Cachet bureau"><div className="pm-toggle-row">{t('cachet_bureau')}<span>{data.cachet_bureau ? 'Activé' : 'Désactivé'}</span></div></Field>
        </div>
      </SectionCard>
    </>
  );
}

function CongesSection({ data, onChange }) {
  const f = (k) => <Input value={data[k]} onChange={v => onChange(k, v)} type="number" />;
  const t = (k) => <Toggle checked={data[k]} onChange={v => onChange(k, v)} />;
  return (
    <>
      <SectionCard title="Droits à congés (jours)" icon="🏖️">
        <div className="pm-grid-3">
          <Field label="Congés annuels">{f('jours_annuels')}</Field>
          <Field label="Congé maladie">{f('jours_maladie')}</Field>
          <Field label="Congé maternité">{f('jours_maternite')}</Field>
          <Field label="Congé paternité">{f('jours_paternite')}</Field>
          <Field label="Congé deuil">{f('jours_deuil')}</Field>
          <Field label="Congé mariage">{f('jours_mariage')}</Field>
        </div>
      </SectionCard>
      <SectionCard title="Règles d'approbation & Quotas" icon="✅">
        <div className="pm-grid-3">
          <Field label="Approbation N+1"><div className="pm-toggle-row"><Toggle checked={data.approbation_n1} onChange={v => onChange('approbation_n1', v)} /><span>{data.approbation_n1 ? 'Requise' : 'Non requise'}</span></div></Field>
          <Field label="Approbation N+2"><div className="pm-toggle-row"><Toggle checked={data.approbation_n2} onChange={v => onChange('approbation_n2', v)} /><span>{data.approbation_n2 ? 'Requise' : 'Non requise'}</span></div></Field>
          <Field label="Report année suivante"><div className="pm-toggle-row"><Toggle checked={data.report_annee_suiv} onChange={v => onChange('report_annee_suiv', v)} /><span>{data.report_annee_suiv ? 'Autorisé' : 'Interdit'}</span></div></Field>
          <Field label="Solde alerte min (jours)"><Input value={data.alerte_solde_min} onChange={v => onChange('alerte_solde_min', v)} type="number" /></Field>
          <Field label="Délai demande min (jours)"><Input value={data.delai_demande} onChange={v => onChange('delai_demande', v)} type="number" /></Field>
        </div>
      </SectionCard>
      <SectionCard title="Période de blocage" icon="🚫">
        <div className="pm-grid-2">
          <Field label="Début blocage (MM-JJ)"><Input value={data.periode_blocage_debut} onChange={v => onChange('periode_blocage_debut', v)} placeholder="12-01" /></Field>
          <Field label="Fin blocage (MM-JJ)"><Input value={data.periode_blocage_fin} onChange={v => onChange('periode_blocage_fin', v)} placeholder="12-31" /></Field>
        </div>
      </SectionCard>
    </>
  );
}

function SecuriteSection({ data, onChange }) {
  const t = (k) => <Toggle checked={data[k]} onChange={v => onChange(k, v)} />;
  return (
    <>
      <SectionCard title="Authentification & Sessions" icon="🔑">
        <div className="pm-grid-3">
          <Field label="Double authentification"><div className="pm-toggle-row">{t('double_auth')}<span>{data.double_auth ? 'Activée' : 'Désactivée'}</span></div></Field>
          <Field label="Durée session (min)"><Input value={data.session_duree} onChange={v => onChange('session_duree', v)} type="number" /></Field>
          <Field label="Expiration MDP (jours)"><Input value={data.mdp_expiration} onChange={v => onChange('mdp_expiration', v)} type="number" /></Field>
        </div>
      </SectionCard>
      <SectionCard title="Règles de mot de passe" icon="🛡️">
        <div className="pm-grid-3">
          <Field label="Longueur minimale"><Input value={data.mdp_longueur_min} onChange={v => onChange('mdp_longueur_min', v)} type="number" /></Field>
          <Field label="Majuscule requise"><div className="pm-toggle-row">{t('mdp_majuscule')}<span>{data.mdp_majuscule ? 'Oui' : 'Non'}</span></div></Field>
          <Field label="Chiffre requis"><div className="pm-toggle-row">{t('mdp_chiffre')}<span>{data.mdp_chiffre ? 'Oui' : 'Non'}</span></div></Field>
          <Field label="Caractère spécial requis"><div className="pm-toggle-row">{t('mdp_special')}<span>{data.mdp_special ? 'Oui' : 'Non'}</span></div></Field>
        </div>
      </SectionCard>
      <SectionCard title="Accès & Journalisation" icon="📋">
        <div className="pm-grid-2">
          <Field label="Logs connexion"><div className="pm-toggle-row">{t('logs_connexion')}<span>{data.logs_connexion ? 'Activés' : 'Désactivés'}</span></div></Field>
          <Field label="Restriction IP"><div className="pm-toggle-row">{t('restriction_ip')}<span>{data.restriction_ip ? 'Activée' : 'Désactivée'}</span></div></Field>
          {data.restriction_ip && (
            <Field label="Adresses IP autorisées" hint="Séparer par des virgules">
              <Input value={data.ip_whitelist} onChange={v => onChange('ip_whitelist', v)} placeholder="192.168.1.0/24, 10.0.0.1" />
            </Field>
          )}
        </div>
      </SectionCard>
    </>
  );
}

function NotifSection({ data, onChange }) {
  const t = (k) => <Toggle checked={data[k]} onChange={v => onChange(k, v)} />;
  return (
    <>
      <SectionCard title="Alertes email" icon="📧">
        <div className="pm-grid-3">
          <Field label="Congés"><div className="pm-toggle-row">{t('email_conges')}<span>{data.email_conges ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="Contrats"><div className="pm-toggle-row">{t('email_contrats')}<span>{data.email_contrats ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="Bulletins"><div className="pm-toggle-row">{t('email_bulletins')}<span>{data.email_bulletins ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="Documents"><div className="pm-toggle-row">{t('email_documents')}<span>{data.email_documents ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="SMS d'alerte"><div className="pm-toggle-row">{t('sms_alerte')}<span>{data.sms_alerte ? 'Activé' : 'Désactivé'}</span></div></Field>
        </div>
      </SectionCard>
      <SectionCard title="Délais d'alerte (jours avant)" icon="⏰">
        <div className="pm-grid-3">
          <Field label="Échéance contrats"><Input value={data.alerte_echeance_j} onChange={v => onChange('alerte_echeance_j', v)} type="number" /></Field>
          <Field label="Fin période d'essai"><Input value={data.alerte_essai_j} onChange={v => onChange('alerte_essai_j', v)} type="number" /></Field>
          <Field label="Expiration documents"><Input value={data.alerte_doc_j} onChange={v => onChange('alerte_doc_j', v)} type="number" /></Field>
        </div>
      </SectionCard>
      <SectionCard title="Rapports automatiques" icon="📊">
        <div className="pm-grid-3">
          <Field label="Récapitulatif hebdo"><div className="pm-toggle-row">{t('recap_hebdo')}<span>{data.recap_hebdo ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="Récapitulatif mensuel"><div className="pm-toggle-row">{t('recap_mensuel')}<span>{data.recap_mensuel ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="Destinataires DRH" hint="Email(s) séparés par des virgules">
            <Input value={data.destinataires_drh} onChange={v => onChange('destinataires_drh', v)} type="email" />
          </Field>
        </div>
      </SectionCard>
    </>
  );
}

function SystemeSection({ data, onChange, onBackup, onRegenKey, loading }) {
  const t = (k) => <Toggle checked={data[k]} onChange={v => onChange(k, v)} />;
  return (
    <>
      <SectionCard title="Sauvegarde" icon="💾">
        <div className="pm-grid-3">
          <Field label="Sauvegarde automatique"><div className="pm-toggle-row">{t('sauvegarde_auto')}<span>{data.sauvegarde_auto ? 'Activée' : 'Désactivée'}</span></div></Field>
          <Field label="Fréquence">
            <select className="pm-input" value={data.frequence_backup || 'Quotidienne'} onChange={e => onChange('frequence_backup', e.target.value)}>
              <option value="Quotidienne">Quotidienne</option>
              <option value="Hebdomadaire">Hebdomadaire</option>
              <option value="Mensuelle">Mensuelle</option>
            </select>
          </Field>
          <Field label="Rétention logs (jours)"><Input value={data.retention_logs} onChange={v => onChange('retention_logs', v)} type="number" /></Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="pm-btn-secondary" onClick={onBackup} disabled={loading}>
            💾 Lancer une sauvegarde maintenant
          </button>
        </div>
      </SectionCard>
      <SectionCard title="Mode système" icon="🔧">
        <div className="pm-grid-3">
          <Field label="Mode maintenance"><div className="pm-toggle-row">{t('maintenance_mode')}<span>{data.maintenance_mode ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="Mode debug"><div className="pm-toggle-row">{t('debug_mode')}<span>{data.debug_mode ? 'Activé' : 'Désactivé'}</span></div></Field>
          <Field label="API externe"><div className="pm-toggle-row">{t('api_externe')}<span>{data.api_externe ? 'Activée' : 'Désactivée'}</span></div></Field>
          {data.api_externe && (
            <Field label="Webhook URL"><Input value={data.webhook_url} onChange={v => onChange('webhook_url', v)} placeholder="https://..." /></Field>
          )}
        </div>
      </SectionCard>
      <SectionCard title="Clé API" icon="🗝️">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ background: '#f1f3f4', padding: '6px 12px', borderRadius: 6, fontSize: 13, flex: 1 }}>
            {data.api_key ? '••••••••••••••••••••••••••••••••' : 'Non générée'}
          </code>
          <button className="pm-btn-secondary" onClick={onRegenKey} disabled={loading}>
            🔄 Régénérer la clé API
          </button>
        </div>
      </SectionCard>
    </>
  );
}

// ── Generic simple CRUD section factory ──────────────────────────────────────

function SimpleCrudSection({ title, icon, items, onAdd, onEdit, onDelete, loading, nameLabel = 'Nom', descLabel = 'Description', nameKey = 'nom' }) {
  const emptyForm = { [nameKey]: '', description: '' };
  const [form, setForm]     = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = () => {
    const val = (form[nameKey] || '').trim();
    if (!val) return;
    if (editId) { onEdit(editId, form); } else { onAdd(form); }
    setForm(emptyForm); setEditId(null); setShowForm(false);
  };

  const startEdit = (item) => {
    setForm({ [nameKey]: item[nameKey] || '', description: item.description || '' });
    setEditId(item.id); setShowForm(true);
  };

  const cancel = () => { setForm(emptyForm); setEditId(null); setShowForm(false); };

  return (
    <SectionCard title={title} icon={icon}>
      <div style={{ marginBottom: 16 }}>
        <button className="pm-btn-primary" onClick={() => { cancel(); setShowForm(true); }}>
          + Nouveau
        </button>
      </div>

      {showForm && (
        <div className="pm-form-box">
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editId ? `Modifier` : `Nouveau`}</h4>
          <div className="pm-grid-2">
            <Field label={`${nameLabel} *`}>
              <input
                className="pm-input"
                value={form[nameKey] || ''}
                onChange={e => setForm(f => ({ ...f, [nameKey]: e.target.value }))}
                placeholder={nameLabel}
              />
            </Field>
            <Field label={descLabel}>
              <input
                className="pm-input"
                value={form.description || ''}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optionnel"
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="pm-btn-primary" onClick={handleSubmit} disabled={loading || !(form[nameKey] || '').trim()}>
              {editId ? 'Enregistrer' : 'Créer'}
            </button>
            <button className="pm-btn-secondary" onClick={cancel}>Annuler</button>
          </div>
        </div>
      )}

      <table className="pm-table">
        <thead>
          <tr>
            <th>#</th>
            <th>{nameLabel}</th>
            <th>{descLabel}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(!items || items.length === 0) && (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#90a4ae', padding: 24 }}>Aucun élément enregistré</td></tr>
          )}
          {(items || []).map(item => (
            <tr key={item.id}>
              <td style={{ color: '#90a4ae', fontSize: 12 }}>{item.id}</td>
              <td><strong>{item[nameKey]}</strong></td>
              <td style={{ color: '#607d8b' }}>{item.description || '—'}</td>
              <td>
                <button className="pm-btn-icon" onClick={() => startEdit(item)} title="Modifier">✏️</button>
                <button className="pm-btn-icon danger" onClick={() => onDelete(item.id)} title="Supprimer">🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

// ── Catégories professionnelles ───────────────────────────────────────────────

const CATEGORIES_PROF = ['A1','A2','B1','B2','C1','C2','D','Agent de maîtrise','Cadre','Cadre supérieur','Directeur'];

function CategoriesSection({ categories, onAdd, onEdit, onDelete, loading }) {
  const empty = { code: '', libelle: '', description: '', salaire_min: '', salaire_max: '' };
  const [form, setForm]     = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const f = (k) => (
    <input className="pm-input" value={form[k] || ''} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))} />
  );

  const handleSubmit = () => {
    if (!(form.code || '').trim()) return;
    if (editId) { onEdit(editId, form); } else { onAdd(form); }
    setForm(empty); setEditId(null); setShowForm(false);
  };

  const startEdit = (item) => {
    setForm({
      code: item.code || '', libelle: item.libelle || '',
      description: item.description || '',
      salaire_min: item.salaire_min || '', salaire_max: item.salaire_max || '',
    });
    setEditId(item.id); setShowForm(true);
  };

  const cancel = () => { setForm(empty); setEditId(null); setShowForm(false); };

  return (
    <SectionCard title="Catégories professionnelles" icon="📊">
      <p style={{ fontSize: 13, color: '#607d8b', marginTop: 0 }}>
        Grille salariale §3.1 — Ces catégories alimentent le champ <em>Catégorie professionnelle</em> du contrat.
      </p>
      <div style={{ marginBottom: 16 }}>
        <button className="pm-btn-primary" onClick={() => { cancel(); setShowForm(true); }}>
          + Nouvelle catégorie
        </button>
      </div>

      {showForm && (
        <div className="pm-form-box">
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h4>
          <div className="pm-grid-2">
            <Field label="Code *">
              <select className="pm-input" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}>
                <option value="">— Choisir —</option>
                {CATEGORIES_PROF.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Libellé">{f('libelle')}</Field>
            <Field label="Salaire min (FCFA)">
              <input className="pm-input" type="number" value={form.salaire_min || ''} onChange={e => setForm(p => ({ ...p, salaire_min: e.target.value }))} />
            </Field>
            <Field label="Salaire max (FCFA)">
              <input className="pm-input" type="number" value={form.salaire_max || ''} onChange={e => setForm(p => ({ ...p, salaire_max: e.target.value }))} />
            </Field>
            <Field label="Description" hint="Optionnel">{f('description')}</Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="pm-btn-primary" onClick={handleSubmit} disabled={loading || !form.code}>
              {editId ? 'Enregistrer' : 'Créer'}
            </button>
            <button className="pm-btn-secondary" onClick={cancel}>Annuler</button>
          </div>
        </div>
      )}

      <table className="pm-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Libellé</th>
            <th>Salaire min</th>
            <th>Salaire max</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(!categories || categories.length === 0) && (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#90a4ae', padding: 24 }}>Aucune catégorie enregistrée</td></tr>
          )}
          {(categories || []).map(c => (
            <tr key={c.id}>
              <td>
                <span style={{ background: '#e3f2fd', color: '#1565C0', padding: '2px 10px', borderRadius: 20, fontWeight: 700, fontSize: 12 }}>
                  {c.code}
                </span>
              </td>
              <td><strong>{c.libelle || '—'}</strong></td>
              <td style={{ fontSize: 12 }}>{c.salaire_min ? Number(c.salaire_min).toLocaleString('fr-FR') + ' F' : '—'}</td>
              <td style={{ fontSize: 12 }}>{c.salaire_max ? Number(c.salaire_max).toLocaleString('fr-FR') + ' F' : '—'}</td>
              <td style={{ color: '#607d8b' }}>{c.description || '—'}</td>
              <td>
                <button className="pm-btn-icon" onClick={() => startEdit(c)} title="Modifier">✏️</button>
                <button className="pm-btn-icon danger" onClick={() => onDelete(c.id)} title="Supprimer">🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

function ProjetsSection({ projets, onAdd, onEdit, onDelete, loading }) {
  const empty = { nom: '', partenaire: '', subvention: '', budget_code: '', date_debut: '', date_fin: '', statut: 'Actif' };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const sf = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.nom.trim()) return;
    if (editId) { onEdit(editId, form); } else { onAdd(form); }
    setForm(empty); setEditId(null); setShowForm(false);
  };

  const startEdit = (p) => {
    setForm({
      nom: p.nom || '', partenaire: p.partenaire || '', subvention: p.subvention || '',
      budget_code: p.budget_code || '', date_debut: p.date_debut || '', date_fin: p.date_fin || '',
      statut: p.statut || 'Actif',
    });
    setEditId(p.id); setShowForm(true);
  };

  const cancel = () => { setForm(empty); setEditId(null); setShowForm(false); };

  const statusColor = { Actif: '#1b5e20', Suspendu: '#e65100', 'Clôturé': '#37474f' };
  const statusBg    = { Actif: '#e8f5e9', Suspendu: '#fff3e0', 'Clôturé': '#eceff1' };

  return (
    <SectionCard title="Projets / Bailleurs de fonds" icon="📁">
      <div style={{ marginBottom: 16 }}>
        <button className="pm-btn-primary" onClick={() => { cancel(); setShowForm(true); }}>
          + Nouveau projet
        </button>
      </div>

      {showForm && (
        <div className="pm-form-box">
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editId ? 'Modifier le projet' : 'Nouveau projet'}</h4>
          <div className="pm-grid-2">
            <Field label="Nom du projet *"><Input value={form.nom} onChange={sf('nom')} /></Field>
            <Field label="Partenaire / Bailleur"><Input value={form.partenaire} onChange={sf('partenaire')} /></Field>
            <Field label="N° Subvention"><Input value={form.subvention} onChange={sf('subvention')} placeholder="ex : SUB-2024-01" /></Field>
            <Field label="Code budgétaire"><Input value={form.budget_code} onChange={sf('budget_code')} placeholder="ex : BC-IMP-101" /></Field>
            <Field label="Date début"><Input value={form.date_debut} onChange={sf('date_debut')} type="date" /></Field>
            <Field label="Date fin"><Input value={form.date_fin} onChange={sf('date_fin')} type="date" /></Field>
            <Field label="Statut">
              <select className="pm-input" value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                <option value="Actif">Actif</option>
                <option value="Suspendu">Suspendu</option>
                <option value="Clôturé">Clôturé</option>
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="pm-btn-primary" onClick={handleSubmit} disabled={loading || !form.nom.trim()}>
              {editId ? 'Enregistrer' : 'Créer'}
            </button>
            <button className="pm-btn-secondary" onClick={cancel}>Annuler</button>
          </div>
        </div>
      )}

      <table className="pm-table">
        <thead>
          <tr>
            <th>Projet</th>
            <th>Partenaire</th>
            <th>Subvention</th>
            <th>Code budgétaire</th>
            <th>Dates</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(!projets || projets.length === 0) && (
            <tr><td colSpan={7} style={{ textAlign: 'center', color: '#90a4ae', padding: 24 }}>Aucun projet enregistré</td></tr>
          )}
          {(projets || []).map(p => (
            <tr key={p.id}>
              <td><strong>{p.nom}</strong></td>
              <td>{p.partenaire || '—'}</td>
              <td><code style={{ fontSize: 11 }}>{p.subvention || '—'}</code></td>
              <td><code style={{ fontSize: 11 }}>{p.budget_code || '—'}</code></td>
              <td style={{ fontSize: 12, color: '#607d8b' }}>
                {p.date_debut ? p.date_debut.slice(0,10) : '—'} → {p.date_fin ? p.date_fin.slice(0,10) : '—'}
              </td>
              <td>
                <span style={{ background: statusBg[p.statut], color: statusColor[p.statut], padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                  {p.statut}
                </span>
              </td>
              <td>
                <button className="pm-btn-icon" onClick={() => startEdit(p)} title="Modifier">✏️</button>
                <button className="pm-btn-icon danger" onClick={() => onDelete(p.id)} title="Supprimer">🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export default function ParametresPage() {
  const [activeTab, setActiveTab] = useState('organisation');
  const [data, setData]           = useState({});
  const [projets, setProjets]         = useState([]);
  const [departements, setDepartements] = useState([]);
  const [fonctions, setFonctions]       = useState([]);
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [alert, setAlert]         = useState(null); // { type, msg }

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  // ── Chargement initial ──────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [paramsRes, depsRes, foncsRes, catsRes] = await Promise.all([
        api.get('/parametres'),
        api.get('/parametres/departements').catch(() => ({ data: { data: [] } })),
        api.get('/parametres/fonctions').catch(() => ({ data: { data: [] } })),
        api.get('/parametres/categories').catch(() => ({ data: { data: [] } })),
      ]);
      const { projets: p = [], ...rest } = paramsRes.data.data ?? paramsRes.data;
      setData(rest);
      setProjets(p);
      setDepartements(depsRes.data.data ?? depsRes.data ?? []);
      setFonctions(foncsRes.data.data ?? foncsRes.data ?? []);
      setCategories(catsRes.data.data ?? catsRes.data ?? []);
    } catch (err) {
      showAlert('error', 'Impossible de charger les paramètres : ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Modification locale ─────────────────────────────────────────────────────
  const handleChange = (section, key, value) => {
    setData(d => ({
      ...d,
      [section]: { ...(d[section] || {}), [key]: value },
    }));
    setDirty(true);
  };

  // ── Sauvegarde ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (CRUD_TABS.includes(activeTab)) return;
    setSaving(true);
    try {
      const { data: json } = await api.put(`/parametres/${activeTab}`, data[activeTab] || {});
      setDirty(false);
      showAlert('success', json.data?.message || 'Paramètres sauvegardés avec succès.');
    } catch (err) {
      showAlert('error', 'Erreur lors de la sauvegarde : ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Actions système ─────────────────────────────────────────────────────────
  const handleBackup = async () => {
    setSaving(true);
    try {
      const { data: json } = await api.post('/parametres/systeme/backup');
      showAlert('success', json.data?.message || json.message || 'Sauvegarde lancée.');
    } catch (err) {
      showAlert('error', err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenKey = async () => {
    if (!window.confirm('Régénérer la clé API ? L\'ancienne clé sera immédiatement invalidée.')) return;
    setSaving(true);
    try {
      const { data: json } = await api.post('/parametres/securite/regen-api-key');
      setData(d => ({ ...d, securite: { ...(d.securite || {}), api_key: json.data.api_key } }));
      showAlert('success', 'Clé API régénérée avec succès.');
    } catch (err) {
      showAlert('error', err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Projets CRUD ────────────────────────────────────────────────────────────
  const handleProjetAdd = async (form) => {
    setSaving(true);
    try {
      await api.post('/parametres/projets', form);
      showAlert('success', 'Projet créé.');
      await loadAll();
    } catch (err) { showAlert('error', err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };

  const handleProjetEdit = async (id, form) => {
    setSaving(true);
    try {
      await api.put(`/parametres/projets/${id}`, form);
      showAlert('success', 'Projet mis à jour.');
      await loadAll();
    } catch (err) { showAlert('error', err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };

  const handleProjetDelete = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    setSaving(true);
    try {
      await api.delete(`/parametres/projets/${id}`);
      showAlert('success', 'Projet supprimé.');
      await loadAll();
    } catch (err) { showAlert('error', err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };

  // ── Départements CRUD ────────────────────────────────────────────────────────
  const makeCrud = (endpoint, successLabel) => ({
    add: async (form) => {
      setSaving(true);
      try { await api.post(endpoint, form); showAlert('success', `${successLabel} créé(e).`); await loadAll(); }
      catch (err) { showAlert('error', err.response?.data?.message || err.message); }
      finally { setSaving(false); }
    },
    edit: async (id, form) => {
      setSaving(true);
      try { await api.put(`${endpoint}/${id}`, form); showAlert('success', `${successLabel} mis(e) à jour.`); await loadAll(); }
      catch (err) { showAlert('error', err.response?.data?.message || err.message); }
      finally { setSaving(false); }
    },
    del: async (id, label) => {
      if (!window.confirm(`Supprimer ce(tte) ${label} ?`)) return;
      setSaving(true);
      try { await api.delete(`${endpoint}/${id}`); showAlert('success', `${successLabel} supprimé(e).`); await loadAll(); }
      catch (err) { showAlert('error', err.response?.data?.message || err.message); }
      finally { setSaving(false); }
    },
  });

  const deptCrud = makeCrud('/parametres/departements', 'Département');
  const foncCrud = makeCrud('/parametres/fonctions', 'Fonction');
  const catCrud  = makeCrud('/parametres/categories', 'Catégorie');

  const CRUD_TABS = ['projets', 'departements', 'fonctions', 'categories'];
  // ── Rendu ───────────────────────────────────────────────────────────────────
  const sec = data[activeTab] || {};
  const mk  = (k, v) => handleChange(activeTab, k, v);

  const renderTab = () => {
    if (loading) return <div className="pm-loading"><div className="pm-spinner" /><span>Chargement…</span></div>;
    switch (activeTab) {
      case 'organisation':  return <OrgSection     data={sec} onChange={mk} />;
      case 'rh':            return <RhSection      data={sec} onChange={mk} />;
      case 'conges':        return <CongesSection  data={sec} onChange={mk} />;
      case 'securite':      return <SecuriteSection data={sec} onChange={mk} onRegenKey={handleRegenKey} loading={saving} />;
      case 'notifications': return <NotifSection   data={sec} onChange={mk} />;
      case 'systeme':       return <SystemeSection  data={sec} onChange={mk} onBackup={handleBackup} onRegenKey={handleRegenKey} loading={saving} />;
      case 'projets':       return <ProjetsSection  projets={projets} onAdd={handleProjetAdd} onEdit={handleProjetEdit} onDelete={handleProjetDelete} loading={saving} />;
      case 'departements':  return <SimpleCrudSection title="Départements" icon="🏗️" items={departements} nameKey="nom" nameLabel="Nom du département" descLabel="Description" onAdd={deptCrud.add} onEdit={deptCrud.edit} onDelete={(id) => deptCrud.del(id, 'département')} loading={saving} />;
      case 'fonctions':     return <SimpleCrudSection title="Fonctions / Postes" icon="🎯" items={fonctions} nameKey="intitule" nameLabel="Intitulé" descLabel="Description" onAdd={foncCrud.add} onEdit={foncCrud.edit} onDelete={(id) => foncCrud.del(id, 'fonction')} loading={saving} />;
      case 'categories':    return <CategoriesSection categories={categories} onAdd={catCrud.add} onEdit={catCrud.edit} onDelete={(id) => catCrud.del(id, 'catégorie')} loading={saving} />;
      default: return null;
    }
  };

  return (
    <div className="pm-page">
      <style>{`
        .pm-page { padding: 24px; max-width: 1100px; }
        .pm-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
        .pm-header h2 { margin: 0; font-size: 22px; color: #1a2a4a; font-weight: 700; }
        .pm-header-sub { font-size: 13px; color: #607d8b; margin-top: 2px; }
        .pm-tabs { display: flex; gap: 4px; background: #f1f4f8; padding: 4px; border-radius: 10px; margin-bottom: 24px; flex-wrap: wrap; }
        .pm-tab { padding: 7px 14px; border-radius: 7px; border: none; background: none; cursor: pointer; font-size: 13px; color: #546e7a; font-weight: 500; transition: all 0.15s; white-space: nowrap; }
        .pm-tab:hover { background: #fff; color: #1a2a4a; }
        .pm-tab.active { background: #fff; color: #1565C0; font-weight: 700; box-shadow: 0 1px 4px rgba(0,0,0,0.1); }
        .pm-card { background: #fff; border: 1px solid #e0e7ef; border-radius: 10px; margin-bottom: 20px; overflow: hidden; }
        .pm-card-header { display: flex; align-items: center; gap: 10px; padding: 14px 20px; background: #f7f9fc; border-bottom: 1px solid #e0e7ef; }
        .pm-card-icon { font-size: 18px; }
        .pm-card-title { margin: 0; font-size: 15px; font-weight: 600; color: #1a2a4a; }
        .pm-card-body { padding: 20px; }
        .pm-grid-2 { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
        .pm-grid-3 { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
        .pm-field { display: flex; flex-direction: column; gap: 5px; }
        .pm-label { font-size: 12px; font-weight: 600; color: #546e7a; text-transform: uppercase; letter-spacing: .4px; }
        .pm-hint { font-size: 11px; color: #90a4ae; }
        .pm-input { border: 1px solid #cfd8dc; border-radius: 6px; padding: 8px 10px; font-size: 13px; color: #1a2a4a; width: 100%; box-sizing: border-box; outline: none; transition: border-color 0.15s; background: #fff; }
        .pm-input:focus { border-color: #1565C0; box-shadow: 0 0 0 3px rgba(21,101,192,0.1); }
        .pm-toggle { display: inline-flex; align-items: center; cursor: pointer; position: relative; }
        .pm-toggle-track { width: 40px; height: 22px; background: #cfd8dc; border-radius: 11px; transition: background 0.2s; position: relative; }
        input:checked + .pm-toggle-track { background: #1565C0; }
        .pm-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; background: #fff; border-radius: 50%; transition: transform 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
        input:checked ~ .pm-toggle-track .pm-toggle-thumb { transform: translateX(18px); }
        .pm-toggle-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; }
        .pm-toggle-row span { font-size: 13px; color: #37474f; }
        .pm-btn-primary { background: #1565C0; color: #fff; border: none; border-radius: 7px; padding: 9px 18px; font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
        .pm-btn-primary:hover:not(:disabled) { background: #0d47a1; }
        .pm-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .pm-btn-secondary { background: #fff; color: #1565C0; border: 1.5px solid #1565C0; border-radius: 7px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
        .pm-btn-secondary:hover:not(:disabled) { background: #e3f2fd; }
        .pm-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
        .pm-btn-icon { background: none; border: none; cursor: pointer; font-size: 16px; padding: 4px 6px; border-radius: 4px; transition: background 0.1s; }
        .pm-btn-icon:hover { background: #f1f4f8; }
        .pm-btn-icon.danger:hover { background: #ffebee; }
        .pm-save-bar { display: flex; align-items: center; gap: 12px; }
        .pm-dirty-dot { width: 8px; height: 8px; background: #ff9800; border-radius: 50%; }
        .pm-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 16px; }
        .pm-table th { text-align: left; padding: 8px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; color: #546e7a; border-bottom: 2px solid #e0e7ef; }
        .pm-table td { padding: 10px 12px; border-bottom: 1px solid #f1f4f8; color: #263238; }
        .pm-table tr:hover td { background: #f7f9fc; }
        .pm-form-box { background: #f7f9fc; border: 1px solid #e0e7ef; border-radius: 8px; padding: 16px; margin-bottom: 20px; }
        .pm-loading { display: flex; align-items: center; gap: 16px; padding: 60px; justify-content: center; color: #607d8b; }
        .pm-spinner { width: 28px; height: 28px; border: 3px solid #e0e7ef; border-top-color: #1565C0; border-radius: 50%; animation: pm-spin 0.7s linear infinite; }
        @keyframes pm-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* En-tête */}
      <div className="pm-header">
        <div>
          <h2>⚙️ Paramètres</h2>
          <div className="pm-header-sub">Configuration générale de l'application GFM3</div>
        </div>
        <div className="pm-save-bar">
          {dirty && !CRUD_TABS.includes(activeTab) && <><div className="pm-dirty-dot" /><span style={{ fontSize: 12, color: '#ff9800' }}>Modifications non sauvegardées</span></>}
          {!CRUD_TABS.includes(activeTab) && (
            <button className="pm-btn-primary" onClick={handleSave} disabled={saving || !dirty}>
              {saving ? '⏳ Sauvegarde…' : '💾 Sauvegarder'}
            </button>
          )}
        </div>
      </div>

      {/* Alerte */}
      {alert && <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert(null)} />}

      {/* Onglets */}
      <div className="pm-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`pm-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setDirty(false); }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {renderTab()}
    </div>
  );
}
